/* cart-rr.js — Roost & Roam cart enhancements
 * Cross-sell rail (page + drawer), recently-viewed empty state,
 * product-page handle tracking.
 */
(function () {
  'use strict';
  if (window.rrCartEnhancementsLoaded) return;
  window.rrCartEnhancementsLoaded = true;

  // ── Helpers ──────────────────────────────────────────
  function moneyFormat(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\.00$/, '');
  }

  // ── Recommendations fetch ─────────────────────────────
  // Resolve the recommendations endpoint from a DOM data attribute
  // (routes.product_recommendations_url), which is locale/market-safe.
  // Fall back to the bare path only if nothing is wired.
  function recommendationsUrl() {
    var el = document.querySelector('[data-recommendations-url]');
    var url = el && el.dataset.recommendationsUrl;
    if (!url) url = '/recommendations/products';
    return url.replace(/\.json$/, '');
  }

  async function fetchRecs(productId, excludeProductIds) {
    const base = recommendationsUrl(); // e.g. /recommendations/products (locale-safe)

    async function tryIntent(intent) {
      const url = base + '.json?product_id=' + productId + '&limit=8&intent=' + intent;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          console.log('[rr-xs] fetch', intent, 'HTTP', res.status, url);
          return [];
        }
        const data = await res.json();
        const raw = data.products || [];
        const products = raw.filter(function (p) {
          return p.available && p.variants.some(v => v.available) && !excludeProductIds.includes(p.id);
        });
        console.log('[rr-xs] intent=' + intent, '→', products.length, 'usable of', raw.length, '(' + url + ')');
        return products;
      } catch (e) {
        console.log('[rr-xs] fetch error', intent, e);
        return [];
      }
    }

    let recs = await tryIntent('complementary');
    if (recs.length < 2) {
      recs = await tryIntent('related');
    }
    return recs;
  }

  // ── Build page cross-sell (4-card grid) ──────────────
  function renderPageCrossSell(container, products) {
    if (!products.length) return;
    const cards = products.slice(0, 4).map(function (p) {
      const v = p.variants.find(v => v.available);
      const choose = p.variants.length > 1;
      const productUrl = (window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(p.handle);
      const choiceLabel = container.dataset.chooseOptionsLabel;
      const img = p.featured_image
        ? '<img class="rr-xs-card__image" src="' + p.featured_image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '<div class="rr-xs-card__image"></div>';
      return (
        '<div class="rr-xs-card">' +
          img +
          '<div class="rr-xs-card__body">' +
            '<div class="rr-xs-card__name">' + escapeHtml(p.title) + '</div>' +
            '<div class="rr-xs-card__price">' + moneyFormat(v.price) + '</div>' +
            '<button type="button" class="rr-xs-card__add" ' + (choose ? 'data-product-url="' + escapeHtml(productUrl) : 'data-variant-id="' + v.id) + '">' + (choose ? escapeHtml(choiceLabel) : 'Add') + '</button>' +
          '</div>' +
        '</div>'
      );
    });

    container.innerHTML =
      '<div class="rr-cross-sell__heading-row">' +
        '<h3 class="rr-cross-sell__heading">Pairs well with</h3>' +
        '<div class="rr-cross-sell__rule"></div>' +
      '</div>' +
      '<div class="rr-cross-sell__grid">' + cards.join('') + '</div>';

    bindAddButtons(container, false);
  }

  // ── Build drawer cross-sell (compact rows) ────────────
  function renderDrawerCrossSell(container, products) {
    if (!products.length) {
      container.innerHTML = '';
      return;
    }
    const rows = products.slice(0, 2).map(function (p) {
      const v = p.variants.find(v => v.available);
      const choose = p.variants.length > 1;
      const productUrl = (window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(p.handle);
      const choiceLabel = container.dataset.chooseOptionsLabel;
      const img = p.featured_image
        ? '<img class="rr-d-xs__thumb" src="' + p.featured_image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '<div class="rr-d-xs__thumb"></div>';
      return (
        '<div class="rr-d-xs__row">' +
          img +
          '<div>' +
            '<div class="rr-d-xs__name">' + escapeHtml(p.title) + '</div>' +
            '<div class="rr-d-xs__price">' + moneyFormat(v.price) + '</div>' +
          '</div>' +
          '<button type="button" class="rr-d-xs__add" ' + (choose ? 'data-product-url="' + escapeHtml(productUrl) : 'data-variant-id="' + v.id) + '" aria-label="' + escapeHtml(choose ? choiceLabel + ' — ' + p.title : 'Add ' + p.title) + '">' + (choose ? '→' : '+') + '</button>' +
        '</div>'
      );
    });

    container.innerHTML =
      '<div class="rr-d-xs__heading">Pairs well with</div>' +
      rows.join('');

    bindAddButtons(container, true);
  }

  // ── Bind Add buttons ──────────────────────────────────
  function bindAddButtons(container, isDrawer) {
    container.querySelectorAll('[data-variant-id], [data-product-url]').forEach(function (btn) {
      if (btn.dataset.rrBound) return;
      btn.dataset.rrBound = '1';
      btn.addEventListener('click', function () {
        if (btn.dataset.productUrl) {
          window.location.assign(btn.dataset.productUrl);
          return;
        }
        addToCart(btn, isDrawer);
      });
    });
  }

  async function addToCart(btn, isDrawer) {
    const variantId = btn.dataset.variantId;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = isDrawer ? '…' : 'Adding…';

    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      });

      if (!res.ok) throw new Error('add failed');

      // Update totals and counts
      window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      return;

    } catch (e) {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  // ── Init cross-sell ───────────────────────────────────
  var crossSellRunning = false;

  async function initCrossSell() {
    if (crossSellRunning) return;
    crossSellRunning = true;
    try {
      // Diagnostic 1 — are the cross-sell containers in the DOM?
      const pageEl = document.getElementById('rr-cross-sell');
      const drawerEl = document.getElementById('rr-drawer-cross-sell');
      console.log('[rr-xs] containers:', { page: !!pageEl, drawer: !!drawerEl });
      if (!pageEl && !drawerEl) return;

      const res = await fetch('/cart.js');
      const cart = await res.json();

      if (!cart.items || !cart.items.length) {
        console.log('[rr-xs] cart empty — nothing to recommend against');
        return;
      }

      // Seed off the highest-priced item (best cross-sell anchor).
      const anchor = cart.items.reduce(function (top, i) {
        return i.price > top.price ? i : top;
      }, cart.items[0]);
      const cartProductIds = cart.items.map(function (i) { return i.product_id; });
      console.log('[rr-xs] anchor product_id=' + anchor.product_id, 'exclude=', cartProductIds);

      const recs = await fetchRecs(anchor.product_id, cartProductIds);
      if (!recs.length) {
        console.log('[rr-xs] both intents empty — hiding rail');
        return;
      }

      if (pageEl) renderPageCrossSell(pageEl, recs);
      if (drawerEl) renderDrawerCrossSell(drawerEl, recs);
    } catch (e) {
      console.log('[rr-xs] initCrossSell error', e);
    } finally {
      crossSellRunning = false;
    }
  }

  // ── Recently viewed (empty cart page) ────────────────
  async function initRecentlyViewed() {
    const container = document.getElementById('rr-recently-viewed');
    if (!container) return;

    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem('rr_recently_viewed') || '[]');
    } catch (e) {
      handles = [];
    }

    if (!handles.length) return;

    const results = await Promise.all(
      handles.slice(0, 4).map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })
    );

    const products = results.filter(Boolean);
    if (!products.length) return;

    const cards = products.map(function (p) {
      const img = p.featured_image
        ? '<img class="rr-rv-card__image" src="' + p.featured_image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '<div class="rr-rv-card__image"></div>';
      return (
        '<a href="/products/' + p.handle + '" class="rr-rv-card">' +
          img +
          '<div class="rr-rv-card__body">' +
            '<div class="rr-rv-card__name">' + escapeHtml(p.title) + '</div>' +
          '</div>' +
        '</a>'
      );
    });

    container.innerHTML =
      '<div class="rr-recently-viewed__heading-row">' +
        '<h4 class="rr-recently-viewed__heading">Recently viewed</h4>' +
        '<div class="rr-recently-viewed__rule"></div>' +
      '</div>' +
      '<div class="rr-recently-viewed__grid">' + cards.join('') + '</div>';
  }

  // ── Track product handle on PDP ───────────────────────
  function trackRecentlyViewed() {
    const el = document.querySelector('[data-product-handle]');
    if (!el) return;
    const handle = el.dataset.productHandle;
    if (!handle) return;

    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem('rr_recently_viewed') || '[]');
    } catch (e) {
      handles = [];
    }

    handles = handles.filter(function (h) { return h !== handle; });
    handles.unshift(handle);
    handles = handles.slice(0, 10);

    try {
      localStorage.setItem('rr_recently_viewed', JSON.stringify(handles));
    } catch (e) {
      // storage full or blocked
    }
  }

  // ── XSS helper ───────────────────────────────────────
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Boot ─────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    trackRecentlyViewed();
    initRecentlyViewed();
    initCrossSell();
    initDiscountCode();
  }

  // ── Discount code (cart page only) ───────────────────
  // The cart's summary rail is inside #main-cart-items .js-contents, which
  // cart.js replaces on every cart update — so bind via delegation on
  // document once, and it survives re-renders.
  //
  // Shopify's Ajax cart has no discount endpoint, but the storefront exposes
  // a server-side shareable-discount URL: /discount/{CODE}. Hitting it
  // validates the code and stores it on the cart session, then ?redirect=/cart
  // returns the buyer to the cart with the discount applied — order/product
  // discounts then appear in cart.cart_level_discount_applications and the
  // Estimated total updates. Invalid codes simply don't apply (Shopify's own
  // validation); we do no client-side checking.
  var discountBound = false;
  function initDiscountCode() {
    if (discountBound) return;
    discountBound = true;

    function go(box) {
      var input = box.querySelector('#discount-code');
      if (!input) return;
      var code = input.value.trim();
      if (!code) { input.focus(); return; }
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
    }

    // The discount input lives inside <cart-items>, whose debounced `change`
    // listener treats every changed field as a quantity input — running
    // parseInt on the code and calling reportValidity(), which flashes a
    // native validation bubble before our redirect fires. Swallow the change
    // in the capture phase (before it reaches <cart-items>) so cart.js never
    // sees it. Delegated on document so it survives .js-contents re-renders.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.id === 'discount-code') e.stopPropagation();
    }, true);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart-discount__apply');
      if (!btn) return;
      var box = btn.closest('.cart-discount');
      if (box) go(box);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target.closest('#discount-code');
      if (!input) return;
      e.preventDefault();
      var box = input.closest('.cart-discount');
      if (box) go(box);
    });
  }
})();
